import { useParams } from 'react-router-dom';
import { useMemo, useState, useEffect } from 'react';
import MonthlyTable from 'features/drilldown/cashflows/MonthlyTable';
import { PortfolioLayout } from '../Portfolio/PortfolioLayout';
import { RemittanceBackButton } from './RemittanceBackButton';
import { useGetAccountRemittanceRecords } from './getAccountRemittanceRecords_gql';
import {
  recordsToMonthlyTableRows,
  getAvailableYears,
} from './loanRemittanceRecordsToMonthlyTable';
import { decodeDealIdFromParam } from './utils';

export const LoanRemittanceProfile = ({ tableOnly = false }: { tableOnly?: boolean }) => {
  const { dealId: rawDealId, accountId } = useParams<{ dealId?: string; accountId: string }>();
  const dealId = decodeDealIdFromParam(rawDealId);
  const { data, loading, error } = useGetAccountRemittanceRecords(dealId, accountId);

  const entities = data?.entities ?? [];
  const availableYears = useMemo(
    () => (entities.length > 0 ? getAvailableYears(entities) : []),
    [entities],
  );
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  useEffect(() => {
    if (
      availableYears.length > 0 &&
      (selectedYear === null || !availableYears.includes(selectedYear))
    ) {
      setSelectedYear(availableYears[availableYears.length - 1]!);
    }
  }, [availableYears, selectedYear]);

  const monthlyRows = useMemo(() => {
    const year =
      selectedYear ??
      (availableYears.length > 0 ? availableYears[availableYears.length - 1]! : null);
    return recordsToMonthlyTableRows(entities, year);
  }, [entities, selectedYear, availableYears]);

  if (!accountId) {
    return null;
  }

  const hasDealContext = Boolean(dealId);
  const showRealData = hasDealContext && !error && entities.length > 0;

  const header = (
    <div>
      <RemittanceBackButton />
      <div>Remittance Profile for {accountId}</div>
      {!hasDealContext && (
        <div style={{ marginTop: 8, color: 'var(--color-text-secondary, #868e9f)' }}>
          Open this loan from a deal (View Loans) to see remittance history.
        </div>
      )}
      {hasDealContext && !showRealData && !loading && (
        <div style={{ marginTop: 8, color: 'var(--color-text-secondary, #868e9f)' }}>
          {error
            ? `Failed to load remittance data: ${error.message}`
            : 'No remittance records for this loan.'}
        </div>
      )}
      {showRealData && availableYears.length > 1 && (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <label htmlFor="remittance-year-select">Year:</label>
          <select
            id="remittance-year-select"
            value={selectedYear ?? ''}
            onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value, 10) : null)}
            style={{ padding: 4, minWidth: 80 }}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );

  if (tableOnly) {
    if (loading) return <div>Loading remittance data…</div>;
    if (!showRealData) return null;
    return <MonthlyTable monthlyCashFlows={monthlyRows} leftAlignAll={true} />;
  }

  if (loading) {
    return (
      <PortfolioLayout hideSummery={true} summeryChildren={header}>
        <div>Loading remittance data…</div>
      </PortfolioLayout>
    );
  }

  if (!showRealData) {
    return (
      <PortfolioLayout hideSummery={true} summeryChildren={header}>
        <div />
      </PortfolioLayout>
    );
  }

  return (
    <PortfolioLayout hideSummery={true} summeryChildren={header}>
      <MonthlyTable
        monthlyCashFlows={monthlyRows}
        leftAlignAll={true}
        year={selectedYear ?? undefined}
        normalSize={true}
      />
    </PortfolioLayout>
  );
};

export default LoanRemittanceProfile;
